import { useState, useCallback, useRef } from 'react';

/**
 * useUndoRedo Hook
 *
 * Manages undo/redo history for transcript editing
 *
 * @param {*} initialState - Initial state value (normalized to array for block state)
 * @param {number} maxHistory - Maximum history size (default 50)
 * @returns {Object} State and control functions
 */
const useUndoRedo = (initialState, maxHistory = 50) => {
  const safeInitial = Array.isArray(initialState) ? initialState : [];
  const [history, setHistory] = useState([safeInitial]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const nextIndexAfterPushRef = useRef(null);

  /**
   * Push new state to history.
   * Uses a ref for currentIndex so the setHistory callback always slices at the correct position
   * (avoids stale closure bug that could make state undefined and clear the editor).
   */
  const pushState = useCallback(
    (newState) => {
      nextIndexAfterPushRef.current = null;

      setHistory((prevHistory) => {
        const idx = currentIndexRef.current;
        const newHistory = prevHistory.slice(0, idx + 1);
        newHistory.push(newState);

        if (newHistory.length > maxHistory) {
          newHistory.shift();
          nextIndexAfterPushRef.current = newHistory.length - 1;
          return newHistory;
        }

        return newHistory;
      });

      setCurrentIndex((prevIndex) => {
        if (nextIndexAfterPushRef.current !== null) {
          const next = nextIndexAfterPushRef.current;
          nextIndexAfterPushRef.current = null;
          return next;
        }
        const newIndex = prevIndex + 1;
        return newIndex >= maxHistory ? maxHistory - 1 : newIndex;
      });
    },
    [maxHistory]
  );

  /**
   * Undo to previous state
   */
  const undo = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  /**
   * Redo to next state
   */
  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, history.length]);

  /**
   * Reset history to initial state
   */
  const reset = useCallback(() => {
    setHistory([safeInitial]);
    setCurrentIndex(0);
  }, [safeInitial]);

  /**
   * Clear all history
   */
  const clear = useCallback(() => {
    setHistory([history[currentIndex]]);
    setCurrentIndex(0);
  }, [history, currentIndex]);

  return {
    state: history[currentIndex] ?? safeInitial,
    pushState,
    undo,
    redo,
    reset,
    clear,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    historySize: history.length,
    currentIndex,
  };
};

export default useUndoRedo;


